import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import LicenseContext from 'src/contexts/LicenseContext';
import { ItemPackageStyleOptions } from 'src/entities/orderEntity';
import { ProductEntity, ProductRequestEntity } from 'src/entities/ProductEntity';
import { queryKey } from 'src/hooks/useProducts.request';
import { useMutationProduct } from 'src/hooks/useQueryProducts';

import { schema } from './schema';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  updateIsLoading?: (isLoading: boolean) => void;
  editEntity?: ProductEntity;
}

const FormDialog: FC<Props> = memo((
  {
    isOpen,
    onClose,
    updateIsLoading,
    editEntity
  }
) => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { addProduct, updateProduct } = useMutationProduct();

  const haveSelectableCompanies = licenseContext?.config?.selectable_companies?.length > 1;
  const isNew = !editEntity;
  const [companyId, setCompanyId] = useState<number>(0);

  const defaultValues: ProductRequestEntity = useMemo(() => ({
    companyId: 0,
    code: '',
    name: '',
    packingStyle: null,
    weightGPerUnit: null,
    volumeMm3PerUnit: null,
    weightKgPerUnit: null,
    volumeM3PerUnit: null,
  }), []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm<ProductRequestEntity>({
    resolver: yupResolver(schema),
    defaultValues
  });

  useEffect(() => {
    if (editEntity) {
      setValue('code', editEntity.code);
      setValue('name', editEntity.name);
      setValue('packingStyle', editEntity.packingStyle);
      setValue('weightKgPerUnit', editEntity.weightKgPerUnit);
      setValue('volumeM3PerUnit', editEntity.volumeM3PerUnit);
    } else {
      reset(defaultValues);
    }
  }, [editEntity, setValue, reset, defaultValues]);

  const onSubmit: SubmitHandler<ProductRequestEntity> = (data) => {
    if (updateIsLoading) updateIsLoading(true);

    if (editEntity) {
      const updateData = { ...editEntity, ...data };
      updateProduct.mutate(updateData, {
        onSuccess: () => {
          enqueueSnackbar('商品を更新しました');
          // // eslint-disable-next-line no-void
          // void queryClient.invalidateQueries([queryKey]);
          onClose();
        },
        onError: (error: AxiosError<{ message: string }>) => {
          enqueueSnackbar(error.response?.data?.message || '更新に失敗しました', { variant: 'error' });
        },
        onSettled: () => {
          if (updateIsLoading) updateIsLoading(false);
        }
      });
    } else {
      data.companyId = companyId;
      addProduct.mutate(data, {
        onSuccess: () => {
          enqueueSnackbar('商品を追加しました');
          // eslint-disable-next-line no-void
          void queryClient.invalidateQueries([queryKey]);
          onClose();
        },
        onError: (error: AxiosError<{ message: string }>) => {
          enqueueSnackbar(error.response?.data?.message || '追加に失敗しました', { variant: 'error' });
        },
        onSettled: () => {
          if (updateIsLoading) updateIsLoading(false);
        }
      });
    }
  };

  const onChangeCompanyId = (value: number) => {
    setCompanyId(value);
  };

  useEffect(() => {
    if (!editEntity) {
      reset({});
      return;
    }

    reset(editEntity);
    setCompanyId(editEntity.companyId);
  }, [editEntity, reset]);

  useEffect(() => {
    if (!licenseContext?.config) return;

    setCompanyId(licenseContext.config.selected_company_id || 0);
  }, [licenseContext?.config]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {editEntity ? '商品編集' : '商品追加'}
      </DialogTitle>
      <DialogContent>
        <Stack component="form" spacing={2} sx={{ mt: 2 }}>
          {[haveSelectableCompanies, isNew].every((bool) => bool) && (
            <>
              <InputLabel>事業所</InputLabel>
              <Select
                id="companyId"
                margin="dense"
                label="事業所"
                type="number"
                fullWidth
                variant="standard"
                value={`${companyId}`}
                onChange={(e) => onChangeCompanyId(Number(e.target.value))}
                disabled={!haveSelectableCompanies}
              >
                {licenseContext.config.selectable_companies.map((company) => (
                  <MenuItem value={company.id} key={company.id}>{company.name}</MenuItem>
                ))}
              </Select>
            </>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="商品コード"
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  fullWidth
                  variant="standard"
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="商品名"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  variant="standard"
                />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <FormControl
              fullWidth
              variant="standard"
              size="small"
            >
              <InputLabel>荷姿</InputLabel>
              <Controller
                name="packingStyle"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value || ''}
                    label="荷姿"
                    fullWidth
                    variant="standard"
                  >
                    <MenuItem key="" value="">&nbsp;</MenuItem>
                    {ItemPackageStyleOptions.map((style) => (
                      <MenuItem key={style} value={style}>
                        {style}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <Controller
              name="weightKgPerUnit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  label="単位重量(kg)"
                  type="number"
                  error={!!errors.weightKgPerUnit}
                  helperText={errors.weightKgPerUnit?.message}
                  fullWidth
                  variant="standard"
                />
              )}
            />

            <Controller
              name="volumeM3PerUnit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value || ''}
                  label="単位体積(m3)"
                  type="number"
                  error={!!errors.volumeM3PerUnit}
                  helperText={errors.volumeM3PerUnit?.message}
                  fullWidth
                  variant="standard"
                />
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          キャンセル
        </Button>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button onClick={handleSubmit(onSubmit)} variant="contained">
          {editEntity ? '更新' : '追加'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default FormDialog;
